/**
 * <cart-upgrade> — "Upgrade and Save". Converts the cart's one-time line(s) to
 * a subscription by setting Appstle's selling plan on them, then refreshes the
 * cart drawer. Display only drives the line's selling_plan; Appstle handles the
 * actual subscription/discount at checkout.
 */
class CartUpgrade extends HTMLElement {
  constructor() {
    super();
    this.sellingPlanId = this.dataset.sellingPlanId;
    this.productId = String(this.dataset.productId || '');
    this.btn = this.querySelector('[data-cart-upgrade-btn]');
    this.spinner = this.querySelector('[data-cart-upgrade-spinner]');
    this.errorEl = this.querySelector('[data-cart-upgrade-error]');
  }

  connectedCallback() {
    this.btn?.addEventListener('click', () => this.upgrade());
  }

  /* One-time lines eligible for upgrade, embedded server-side so we don't have
     to fetch the cart first. Each: {key, variant, quantity, product, plan}. */
  readLines() {
    const el = this.querySelector('[data-cart-upgrade-lines]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  /* Purchasable Appstle plan id: prefer the one captured from the product page
     (per product), then the value embedded on the line, then the card default. */
  planFor(line) {
    let stored = '';
    try {
      stored = localStorage.getItem('soar:sellingPlan:' + (line.product || this.productId)) || '';
    } catch (e) {
      /* storage blocked */
    }
    return stored || (line.plan != null ? String(line.plan) : '') || this.sellingPlanId || '';
  }

  async upgrade() {
    const lines = this.readLines();
    if (!lines.length) return;

    const adds = [];
    for (const l of lines) {
      const plan = this.planFor(l);
      if (!plan) {
        this.showError('No subscription plan available.');
        return;
      }
      adds.push({ id: l.variant, quantity: l.quantity, selling_plan: Number(plan) });
    }

    this.setLoading(true);
    this.clearError();

    const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
    const updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';
    const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const post = (url, data) =>
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(data),
      }).then((r) => r.json());

    try {
      // 1. Remove the one-time line(s). Keys are current as of the server render
      //    and nothing has mutated the cart yet, so they're valid.
      const updates = {};
      lines.forEach((l) => {
        updates[l.key] = 0;
      });
      await post(updateUrl, { updates });

      // 2. Add the subscription version(s) and render the drawer in the same
      //    call — same /cart/add flow the bundle builder uses successfully.
      const addBody = { items: adds };
      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        addBody.sections = cartEl.getSectionsToRender().map((s) => s.id).join(',');
        addBody.sections_url = window.location.pathname;
      }
      const res = await post(addUrl, addBody);
      if (res && res.status) {
        // The subscription add failed — restore the one-time items so nothing is lost.
        await post(addUrl, { items: lines.map((l) => ({ id: l.variant, quantity: l.quantity })) }).catch(() => {});
        throw new Error(res.description || res.message || 'Could not add subscription.');
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-upgrade', cartData: res });
      }
      if (cartEl) {
        cartEl.classList.remove('is-empty');
        try {
          if (typeof cartEl.renderContents === 'function') cartEl.renderContents(res);
          else if (typeof cartEl.open === 'function') cartEl.open();
        } catch (e) {
          if (typeof cartEl.open === 'function') cartEl.open();
        }
      } else {
        window.location.reload();
      }
    } catch (e) {
      this.showError(e && e.message ? e.message : 'Could not upgrade. Please try again.');
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(on) {
    this.btn?.toggleAttribute('aria-disabled', on);
    this.btn?.classList.toggle('loading', on);
    this.spinner?.classList.toggle('hidden', !on);
  }

  showError(msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }
}

if (!customElements.get('cart-upgrade')) customElements.define('cart-upgrade', CartUpgrade);
